import React from 'react';
import { createStyles, Theme, makeStyles, createMuiTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import Container from '@material-ui/core/Container';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Drawer from '@material-ui/core/Drawer';
import Dialog from '@material-ui/core/Dialog';
import MenuIcon from '@material-ui/icons/Menu';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import { _view } from './redux/appSlice';
import { useSelector } from 'react-redux';
import { trackPageView } from './utils/google-analytics';
import Home from './components/Home';
import DrawerItems from './components/DrawerItems';
import NormalDID from './components/NormalDID';
import HDDID from './components/HDDID';
import ResolveDID from './components/ResolveDID';
import OCDIDCreator from './components/OffchainDIDCreator';
import OCDIDResolver from './components/OffchainDIDResolver';
import OCDIDUpdater from './components/OffchainDIDUpdater';
import OCDIDRevoker from './components/OffchainDIDRevoker';
import WizardUI from './components/wizard-ui';
import HeaderLogo from './components/ui/HeaderLogo';
import { Button, Slide, useMediaQuery } from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import MailOutlineIcon from '@material-ui/icons/MailOutline';

const drawerWidth = 200;
const collapsedWidth = 64;

const theme = createMuiTheme({
    palette: {
        primary: {
            main: '#24C3B5',
            contrastText: '#1a1a1a'
        },
        secondary: {
            main: '#24C3B5',
            contrastText: '#ffcc00'
        },
        text: {
            primary: '#5F6F81',
            secondary: '#303030'
        },
        contrastThreshold: 3,
        tonalOffset: 0.2
    }
});

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex'
        },
        appBar: {
            zIndex: theme.zIndex.drawer + 1,
            boxShadow: 'none !important',
            paddingTop: 12,
            backgroundColor: 'transparent !important'
        },
        appBarScrolled: {
            backgroundColor: '#ffffff !important',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            transition: 'background-color 0.25s ease'
        },
        toolbar: {
            paddingLeft: 16,
            paddingRight: 16
        },
        headerPill: {
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            borderRadius: 50,
            background: '#24C3B5',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(36, 195, 181, 0.1)',
            border: '1px solid rgba(36, 195, 181, 0.18)',
            padding: '8px 20px 8px 6px'
        },
        drawer: {
            flexShrink: 0,
            transition: 'width 0.25s ease'
        },
        drawerPaperMobile: {
            width: drawerWidth
        },
        mobileNav: {
            borderRadius: 24,
            width: 300,
            maxWidth: '90vw',
            height: '100%',
            margin: 0,
            background: 'rgba(255, 255, 255, 0.96)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(36, 195, 181, 0.15)',
            overflow: 'hidden',
            overflowY: 'auto' as const
        },
        drawerPaperDesktop: {
            top: 93,
            left: 16,
            height: 'calc(100% - 106px)',
            borderRadius: 24,
            background: 'rgba(255, 255, 255, 0.92)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.10), 0 2px 8px rgba(36, 195, 181, 0.08)',
            border: '1px solid rgb(46, 196, 182, 0.15)',
            overflow: 'hidden',
            overflowX: 'hidden' as const,
            whiteSpace: 'nowrap' as const,
            transition: 'width 0.25s ease'
        },
        content: {
            flexGrow: 1,
            padding: theme.spacing(3),
            [theme.breakpoints.up('md')]: {
                paddingLeft: theme.spacing(6)
            }
        },
        menuButton: {
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
            color: '#fff',
            [theme.breakpoints.up('sm')]: {
                display: 'none'
            }
        }
    })
);

const VIEW_PAGES: Record<number, string> = {
    0: '/home',
    1: '/create-did',
    2: '/create-hd-did',
    3: '/resolve-did',
    4: '/credential-studio',
    5: '/create-offchain-did',
    6: '/resolve-offchain-did',
    7: '/update-offchain-did',
    8: '/revoke-offchain-did'
};

const VIEW_TITLES: Record<number, string> = {
    0: 'ZeDeId Tools — Decentralized Identity Toolkit',
    1: 'Create DID — ZeDeId Tools',
    2: 'Create HD DID — ZeDeId Tools',
    3: 'Resolve DID — ZeDeId Tools',
    4: 'Credential Studio — ZeDeId Tools',
    5: 'Create Offchain DID — ZeDeId Tools',
    6: 'Resolve Offchain DID — ZeDeId Tools',
    7: 'Update Offchain DID — ZeDeId Tools',
    8: 'Revoke Offchain DID — ZeDeId Tools'
};

const SLIDE_DURATION = 650;

const NavTransition = React.forwardRef(function NavTransition(
    props: TransitionProps & { children?: React.ReactElement<any, any> },
    ref: React.Ref<unknown>
) {
    return <Slide direction="left" ref={ref} timeout={SLIDE_DURATION} {...props} />;
});

function App() {
    const view = useSelector(_view);
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [scrolled, setScrolled] = React.useState(false);
    const [collapsed, setCollapsed] = React.useState(false);
    const classes = useStyles();

    const currentDrawerWidth = collapsed ? collapsedWidth : drawerWidth;

    // Detect scroll
    React.useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    React.useEffect(() => {
        trackPageView(VIEW_PAGES[view] ?? '/home');
        document.title = VIEW_TITLES[view] ?? 'ZeDeId Tools — Decentralized Identity Toolkit';
    }, [view]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    let currentView = <div></div>;

    switch (view) {
        case 0:
            currentView = <Home />;
            break;
        case 1:
            currentView = <NormalDID />;
            break;
        case 2:
            currentView = <HDDID />;
            break;
        case 3:
            currentView = <ResolveDID />;
            break;
        case 4:
            currentView = <WizardUI />;
            break;
        case 5:
            currentView = <OCDIDCreator />;
            break;
        case 6:
            currentView = <OCDIDResolver />;
            break;
        case 7:
            currentView = <OCDIDUpdater />;
            break;
        case 8:
            currentView = <OCDIDRevoker />;
            break;
        default:
            currentView = <Home />;
    }

    const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

    return (
        <ThemeProvider theme={theme}>
            <div className={classes.root}>
                <AppBar
                    position="fixed"
                    className={`${classes.appBar} ${scrolled ? classes.appBarScrolled : ''}`}>
                    <Toolbar className={classes.toolbar}>
                        <div className={classes.headerPill}>
                            <IconButton
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                className={classes.menuButton}>
                                <MenuIcon />
                            </IconButton>

                            <Hidden xsDown>
                                <HeaderLogo />
                            </Hidden>
                            <div style={{ flex: 1 }} />
                            <Button
                                href="https://zedeid.com/#contact"
                                target="_blank"
                                rel="noopener noreferrer"
                                endIcon={<MailOutlineIcon style={{ fontSize: 15 }} />}
                                style={{
                                    backgroundColor: '#fff',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: 8,
                                    color: '#6b7280',
                                    padding: '10px 20px',
                                    fontFamily: "'DM Mono', monospace",
                                    transition: 'transform 0.15s ease, border-color 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLElement).style.transform =
                                        'translateY(-1px)';
                                    (e.currentTarget as HTMLElement).style.borderColor = '#2ec4b6';
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLElement).style.transform =
                                        'translateY(0)';
                                    (e.currentTarget as HTMLElement).style.borderColor = '#d1d5db';
                                }}>
                                Get in touch
                            </Button>
                        </div>
                    </Toolbar>
                </AppBar>

                <Hidden smUp implementation="css">
                    <Dialog
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        TransitionComponent={NavTransition}
                        transitionDuration={{ enter: SLIDE_DURATION, exit: SLIDE_DURATION }}
                        PaperProps={{ className: classes.mobileNav }}
                        BackdropProps={{
                            style: {
                                backgroundColor: 'rgba(255, 255, 255, 0.95)'
                            }
                        }}>
                        <DrawerItems
                            onNavigate={() => setMobileOpen(false)}
                            onClose={() => setMobileOpen(false)}
                        />
                    </Dialog>
                </Hidden>
                <Hidden xsDown implementation="css">
                    <Drawer
                        className={classes.drawer}
                        style={{ width: currentDrawerWidth }}
                        variant="permanent"
                        classes={{ paper: classes.drawerPaperDesktop }}
                        PaperProps={{ style: { width: currentDrawerWidth } }}
                        anchor="left">
                        <DrawerItems
                            collapsed={collapsed}
                            onToggleCollapse={() => setCollapsed(!collapsed)}
                        />
                    </Drawer>
                </Hidden>

                <main className={classes.content}>
                    <div style={{ height: 50 }} />
                    <Container
                        maxWidth={false}
                        style={{
                            marginTop: 25,
                            paddingRight: isSmUp ? 0 : undefined
                        }}>
                        {currentView}
                    </Container>
                </main>
            </div>
        </ThemeProvider>
    );
}

export default App;
